import { Dictionary, uniq } from 'lodash';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useQueryClient } from 'react-query';
import { Role, User, UserType } from 'api/models';
import { ProjectUserLite } from 'api/project';
import { AvailabilityForTeamWithProject } from 'api/resource-management/types';
import { Button } from 'components/atoms/button';
import { FilterDrawer } from 'components/molecules/filter-drawer';
import { Filter } from 'components/molecules/filters/types';
import { defaultFilter } from 'components/molecules/filters/utils';
import { TaskFilterButton } from 'components/molecules/views/components';
import { useUserCustomFieldsAsFilterableFields } from 'components/organisms/module-settings/team-members/section-field/hooks';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { SingleSelectFieldFilter } from 'components/organisms/tasks/filters/single-select';
import { getFilterFunction } from 'components/organisms/tasks/get-filter-function';
import { useTranslation } from 'hooks/use-translation';
import { getProjectUsersLiteKey } from 'queries/common';
import { useActiveOrInvitedNonCustomerUsersQuery } from 'queries/members';
import { useNativeTeamsInAccount } from 'queries/placeholder';
import { useVendorRolesQuery } from 'queries/roles';
import { fullName } from 'utils/full-name';
import { useUserFieldsAsFilterableFields } from 'utils/user-fields';
import { RoleSelectionDropdown } from '../../role-selection';
import { ProjectsWithMembers, ReplacementUserForTeam } from '../../types';
import { userAsFilterFormat } from '../../utils/resource-matching';
import { getUserRoleOptions } from '../../utils/role-options';
import { ModalContent } from './common';
import { columnsForFindMember } from './find-member-utils';

interface Props {
  projectId: number;
  projectLookup: Dictionary<ProjectsWithMembers>;
  teamId: number;
  teamName: string;
  availableResources?: AvailabilityForTeamWithProject;
  selectedReplacementUsers: ReplacementUserForTeam[];
  updateSelectedReplacementUsers: (users: ReplacementUserForTeam[]) => void;
}

export const FindMemberContent: FC<Props> = ({
  projectId,
  projectLookup,
  teamId,
  teamName,
  availableResources,
  selectedReplacementUsers,
  updateSelectedReplacementUsers,
}) => {
  const queryClient = useQueryClient();

  const { data: users = [] } = useActiveOrInvitedNonCustomerUsersQuery();
  const { data: roles = [] } = useVendorRolesQuery();
  const { data: teams = [] } = useNativeTeamsInAccount();
  const { fields: userCustomFields } = useUserCustomFieldsAsFilterableFields();
  const { fields: allUserFields, isFieldsLoading: isUserFieldsLoading } =
    useUserFieldsAsFilterableFields();

  const usersLiteInProject = useMemo(
    () =>
      queryClient.getQueryData<Dictionary<ProjectUserLite[]>>(
        getProjectUsersLiteKey(),
      )?.[projectId] ?? [],
    [projectId, queryClient],
  );

  const alreadyAssignedUserIds = useMemo(
    () =>
      usersLiteInProject.length > 0
        ? usersLiteInProject
            .filter(({ referenceTeam }) => referenceTeam?.teamName === teamName)
            .map(({ user: { userId } }) => userId)
        : [],
    [teamName, usersLiteInProject],
  );

  const usersNotAlreadyAssigned = useMemo(() => {
    const partnersInProject = (projectLookup[projectId]?.partners ?? []).map(
      ({ companyId }) => companyId,
    );

    return users.filter(
      ({ userId, userType, company: { companyId } }) =>
        !alreadyAssignedUserIds.includes(userId) &&
        (userType === UserType.Partner
          ? partnersInProject.includes(companyId)
          : true),
    );
  }, [alreadyAssignedUserIds, projectId, projectLookup, users]);

  const userFullfilledMinutesLookup =
    availableResources != null
      ? availableResources.matches.reduce(
          (final, { user: { userId }, fulfilledMinutes }) => ({
            ...final,
            [userId]: fulfilledMinutes <= 0 ? 0 : fulfilledMinutes,
          }),
          {},
        )
      : {};

  const projectUserIds = useMemo(
    () =>
      uniq(
        projectLookup[projectId]?.projectMembers?.map(
          ({ user: { userId } }) => userId,
        ) ?? [],
      ),
    [projectId, projectLookup],
  );

  const matchingUsersForTeam: ReplacementUserForTeam[] =
    usersNotAlreadyAssigned.map((user) => ({
      ...user,
      fullfilledMinutes: userFullfilledMinutesLookup[user.userId] ?? 0,
      isInProject: projectUserIds.includes(user.userId),
    }));

  const defaultRoleId = teams.find((team) => team.teamId === teamId)?.roleId;
  const defaultRole = roles.filter(({ roleId }) => roleId === defaultRoleId);

  const [choosenRoles, setChoosenRoles] = useState<Role[]>(defaultRole);
  const choosenRoleIds = useMemo(
    () => choosenRoles.map(({ roleId }) => roleId),
    [choosenRoles],
  );
  const getDefaultFilter = useCallback((): Filter => {
    const roleField = allUserFields.find(
      ({ fieldName }) => fieldName === 'role',
    );

    if (roleField != null) {
      return {
        ...defaultFilter,
        criteria: [
          {
            field: roleField,
            type: SelectFilterOperationType.IsOneOf,
            value: [defaultRoleId],
          } as SingleSelectFieldFilter,
        ],
      };
    }

    return defaultFilter;
  }, [allUserFields, defaultRoleId]);
  const [filter, setFilter] = useState<Filter>(getDefaultFilter());
  const validFilters = useMemo(
    () => filter.criteria.filter(FieldFilter.isValid),
    [filter.criteria],
  );
  const filterFunction = useMemo(() => {
    return getFilterFunction(filter) ?? (() => true);
  }, [filter]);

  const searchPlaceholder = useTranslation(
    'resource_management.find_member.search.placeholder',
  );

  const combinedFilterFunction = useCallback(
    (user: User, searchText: string) =>
      (userCustomFields.length > 0 ||
        choosenRoleIds.length === 0 ||
        Boolean(
          user.role?.roleId && choosenRoleIds.includes(user.role?.roleId),
        )) &&
      Boolean(
        searchText === '' ||
          fullName(user).toLowerCase().includes(searchText.toLowerCase()),
      ) &&
      (userCustomFields.length === 0 ||
        filterFunction?.(userAsFilterFormat(user))),
    [choosenRoleIds, filterFunction, userCustomFields.length],
  );
  const [filterDrawerVisible, setFilterDrawerVisibility] = useState(false);
  const actionBar =
    userCustomFields.length > 0 ? (
      <>
        <TaskFilterButton
          onClick={() => setFilterDrawerVisibility(!filterDrawerVisible)}
          loading={isUserFieldsLoading}
        >
          <FormattedMessage
            id="captions.buttons.filter"
            values={{
              count: validFilters.length,
            }}
          />
        </TaskFilterButton>
        {validFilters.length > 0 ? (
          <Button
            size="small"
            type="link"
            onClick={() => {
              setFilter(defaultFilter);
            }}
          >
            <FormattedMessage id="captions.buttons.clear_filter" />
          </Button>
        ) : null}

        <FilterDrawer
          title={projectLookup[projectId]?.projectName}
          visible={filterDrawerVisible}
          filter={filter}
          onSubmit={(next) => {
            setFilter(next);
            setFilterDrawerVisibility(false);
          }}
          onClose={() => setFilterDrawerVisibility(false)}
          fields={allUserFields}
        />
      </>
    ) : (
      <RoleSelectionDropdown
        roleOptions={getUserRoleOptions(roles, usersNotAlreadyAssigned)}
        numberOfEntities={usersNotAlreadyAssigned.length}
        choosenRoles={choosenRoles}
        onRoleSelectionChange={setChoosenRoles}
      />
    );

  return (
    <ModalContent
      isMultiSelect={false}
      entityIdKey="userId"
      actionBar={actionBar}
      entities={matchingUsersForTeam}
      searchPlaceholder={searchPlaceholder}
      filterFunction={combinedFilterFunction}
      selectedEntities={selectedReplacementUsers}
      updateSelectedEntities={updateSelectedReplacementUsers}
      columns={columnsForFindMember(
        teamName,
        availableResources?.requiredMinutes ?? 0,
      )}
    />
  );
};
